import { DateTime, Duration } from 'luxon'
import {
  type DateInput,
  type DateRange,
  isDefined,
  isNotDefined,
  type OptionalDateRange,
  pluralize,
  REPORT_TIME_PERIODS,
  type RangeFilter,
  SECOND,
  type PersistedDateRange,
  type PersistedRelativeDateRange,
} from '@terros/common'
import { type TranslateFunction } from './i18n'

export const fromDateInput = (date: DateInput): DateTime => {
  if (typeof date === 'number') {
    return DateTime.fromMillis(date)
  }
  return DateTime.fromISO(date)
}

export const fromOptionalDateInput = (date: DateInput | undefined): DateTime | undefined => {
  if (isDefined(date)) return fromDateInput(date)
}

const YEAR_FORMAT = ', yyyy'
export const formatOptionalDateRange = (
  range: OptionalDateRange | undefined,
  defaultLabel: string = 'All Time'
): string => {
  const startDate = fromOptionalDateInput(range?.startTime)
  const endDate = fromOptionalDateInput(range?.endTime)

  const now = DateTime.now()

  const matchingPeriod = findMatchingPersistedRange(range)
  if (matchingPeriod) return stringifyPersistedDateRange(matchingPeriod)

  if (isDefined(startDate) && isDefined(endDate)) {
    return formatDateRange({ startTime: startDate.toMillis(), endTime: endDate.toMillis() })
  }

  if (isDefined(startDate)) {
    const yearDateFormat = startDate.hasSame(now, 'year') ? 'MMM d' : `MMM d${YEAR_FORMAT}`
    return `After ${startDate.toFormat(yearDateFormat)}`
  }
  if (isDefined(endDate)) {
    const startDateFormat = endDate.hasSame(now, 'year') ? 'MMM d' : `MMM d${YEAR_FORMAT}`
    return `Before ${endDate.toFormat(startDateFormat)}`
  }
  return defaultLabel
}

export const formatDateRange = (range: DateRange): string => {
  const matchingPeriod = findMatchingPersistedRange(range)
  if (matchingPeriod) return stringifyPersistedDateRange(matchingPeriod)

  const startDate = fromDateInput(range.startTime)
  const endDate = fromDateInput(range.endTime)

  const now = DateTime.now()

  let startDateFormat = 'MMM d'
  let endDateFormat = 'MMM d'

  if (startDate && endDate) {
    if (startDate.hasSame(endDate, 'year')) {
      if (startDate.hasSame(endDate, 'month')) {
        startDateFormat = 'MMM d'
        endDateFormat = 'd'
      } else {
        startDateFormat = 'MMM d'
        endDateFormat = 'MMM d'
      }
    }
    if (!startDate.hasSame(now, 'year')) {
      endDateFormat = endDateFormat + YEAR_FORMAT
    }
  }
  return `${startDate.toFormat(startDateFormat)} - ${endDate.toFormat(endDateFormat)}`
}

export const findMatchingPersistedRange = (range?: OptionalDateRange): PersistedDateRange | undefined => {
  const now = DateTime.now()

  if (!range || (isNotDefined(range.startTime) && isNotDefined(range.endTime))) return { type: 'allTime' }

  const currentPeriod = REPORT_TIME_PERIODS.find((period) => {
    return now.endOf(period).toMillis() === range.endTime && now.startOf(period).toMillis() === range.startTime
  })
  if (currentPeriod) {
    return { type: 'relative', period: currentPeriod, offset: 0 }
  }

  const previousPeriod = REPORT_TIME_PERIODS.find((period) => {
    const offset = now.minus({ [period]: 1 })
    return offset.endOf(period).toMillis() === range.endTime && offset.startOf(period).toMillis() === range.startTime
  })
  if (previousPeriod) {
    return { type: 'relative', period: previousPeriod, offset: -1 }
  }
}

export const stringifyRelativeTimeDiff = (date: number, now: number, t: TranslateFunction): string => {
  if (date <= 0) return t('N/A')
  if (date - now === 0) return t('just now')
  const nowDt = DateTime.fromMillis(now)
  const dateDt = DateTime.fromMillis(date)
  if (!nowDt.isValid || !dateDt.isValid) return t('N/A')

  const getTimeDiff = (now: DateTime, date: DateTime): string => {
    const relative = date.toRelative({ base: now })
    if (isNotDefined(relative)) return t('N/A')
    return t(relative)
  }

  const days = nowDt.diff(dateDt, 'days').days
  if (days <= 1) return getTimeDiff(nowDt, dateDt)

  const nowStart = nowDt.startOf('day')
  const dateStart = dateDt.startOf('day')
  return getTimeDiff(nowStart, dateStart)
}

export const parseSecondsToTimestamp = (seconds: number): string => {
  const millis = seconds * SECOND
  return Duration.fromMillis(millis).toFormat('mm:ss')
}

export const ROLLING_PRESET_DAYS = [2, 3, 7]
export const OLDER_THAN_PRESET_DAYS = [7, 14, 30, 60, 90, 120]

export const fromPersistedDateRange = (range: PersistedDateRange): OptionalDateRange => {
  if (range.type === 'custom') return range
  if (range.type === 'allTime') return {}
  if (range.type === 'rolling') {
    const now = DateTime.now()
    return {
      startTime: now
        .minus({ days: range.days - 1 })
        .startOf('day')
        .toMillis(),
      endTime: now.endOf('day').toMillis(),
    }
  }
  if (range.type === 'olderThan') {
    return { endTime: DateTime.now().minus({ days: range.days }).endOf('day').toMillis() }
  }
  const { period, offset } = range
  const now = DateTime.now()

  const withOffset = now.plus({ [period]: offset })
  const start = withOffset.startOf(period)
  const end = withOffset.endOf(period)

  return { startTime: start.toMillis(), endTime: end.toMillis() }
}

export const stringifyPersistedDateRange = (range: PersistedDateRange): string => {
  if (range.type === 'custom') return formatOptionalDateRange(range)
  if (range.type === 'allTime') return 'All Time'
  if (range.type === 'rolling') return `Last ${range.days} ${pluralize(range.days, 'day')}`
  if (range.type === 'olderThan') return `${range.days}+ days ago`
  return stringifyRelativeRange(range)
}

export const rangeFilterToOptionalDateRange = (filter: RangeFilter | undefined): OptionalDateRange | undefined => {
  if (!filter) return undefined
  return { startTime: filter.gte, endTime: filter.lte }
}

export const rangeFilterfromOptionalDateRange = (filter: OptionalDateRange | undefined): RangeFilter | undefined => {
  if (!filter) return undefined
  return {
    gte: fromOptionalDateInput(filter.startTime)?.toMillis(),
    lte: fromOptionalDateInput(filter.endTime)?.toMillis(),
  }
}

export const stringifyRelativeRange = (range: PersistedRelativeDateRange): string => {
  const { period, offset } = range

  if (offset && offset !== -1) {
    return formatOptionalDateRange(fromPersistedDateRange(range))
  }

  if (period === 'day') {
    if (offset === -1) return 'Yesterday'
    return 'Today'
  }

  const prefix = offset === -1 ? 'Last' : 'This'

  switch (period) {
    case 'hour':
      return `${prefix} Hour`
    case 'week':
      return `${prefix} Week`
    case 'quarter':
      return `${prefix} Quarter`
    case 'month':
      return `${prefix} Month`
    case 'year':
      return `${prefix} Year`
  }
}
